import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

// โหลดรูปบัตร/รูปถือบัตรเป็น blob (endpoint ต้องใช้ token) แล้วแสดง thumbnail พร้อมลายน้ำ คลิกเพื่อซูม
// img: 'card' (รูปบัตร, ค่าเริ่ม) | 'face' (รูปถ่ายถือบัตร)
function IdCardThumb({ reqId, onZoom, img = 'card' }) {
    const [url, setUrl] = useState('');
    const [err, setErr] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        (async () => {
            try {
                // ขอรูปย่อ (thumbnail) สำหรับแสดงในลิสต์ — เล็กกว่ารูปเต็ม ~99% โหลดเร็วขึ้นมาก
                const params = { variant: 'thumb' };
                if (img === 'face') params.img = 'face';
                const res = await api.get(`/admin/account-requests/${reqId}/id-card`, { params, responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                if (!cancelled) setUrl(objectUrl);
                else URL.revokeObjectURL(objectUrl);
            } catch (e) {
                if (!cancelled) setErr(true);
            }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [reqId, img]);

    if (err) return <span style={{ color: '#dc2626', fontSize: 13 }}>โหลดรูปไม่ได้</span>;
    if (!url) return <span className="idcard-thumb-loading"><span className="spinner-xs"></span></span>;

    return (
        <button type="button" className="idcard-thumb" onClick={() => onZoom(reqId, img)} title="คลิกเพื่อดูขนาดเต็ม">
            <img src={url} alt={img === 'face' ? 'รูปถ่ายถือบัตรประชาชน' : 'บัตรประจำตัวประชาชน'} />
            <span className="idcard-watermark" aria-hidden="true" />
            <span className="idcard-thumb-zoom" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                </svg>
            </span>
        </button>
    );
}

// Lightbox แสดงรูปขนาดเต็ม (โหลด blob ของตัวเอง — ไม่ผูก lifecycle กับ thumbnail)
function IdCardLightbox({ reqId, fullname, img = 'card', onClose }) {
    const [url, setUrl] = useState('');
    const [err, setErr] = useState(false);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        (async () => {
            try {
                const params = img === 'face' ? { img: 'face' } : {};
                const res = await api.get(`/admin/account-requests/${reqId}/id-card`, { params, responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                if (!cancelled) setUrl(objectUrl);
                else URL.revokeObjectURL(objectUrl);
            } catch (e) {
                if (!cancelled) setErr(true);
            }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [reqId, img]);

    return (
        <div className="idcard-lightbox-overlay" onClick={onClose}>
            <div className="idcard-lightbox" onClick={(e) => e.stopPropagation()}>
                <div className="idcard-lightbox-head">
                    <span>{img === 'face' ? 'รูปถ่ายถือบัตรประชาชน' : 'รูปบัตรประชาชน'}{fullname ? ` — ${fullname}` : ''}</span>
                    <button className="idcard-lightbox-close" onClick={onClose} aria-label="ปิด">&times;</button>
                </div>
                <div className="idcard-lightbox-body">
                    {err ? (
                        <p style={{ color: '#fca5a5', padding: 40 }}>ไม่สามารถโหลดรูปได้</p>
                    ) : !url ? (
                        <div className="spinner" />
                    ) : (
                        <div className="idcard-lightbox-imgwrap">
                            <img src={url} alt="บัตรประจำตัวประชาชน" />
                            <span className="idcard-watermark" aria-hidden="true" />
                        </div>
                    )}
                </div>
                <div className="idcard-lightbox-foot">
                    เอกสารนี้เป็นข้อมูลส่วนบุคคล — ใช้เพื่อยืนยันตัวตนในการเปิดบัญชีเท่านั้น
                </div>
            </div>
        </div>
    );
}

function AccountRequests() {
    const [requests, setRequests] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [lightbox, setLightbox] = useState(null); // { reqId, fullname }
    const [resendingId, setResendingId] = useState(null); // reqId กำลังส่งอีเมลแจ้งซ้ำ
    const [revealedPw, setRevealedPw] = useState({}); // { [reqId]: { password, encrypted } } ที่กดแสดงแล้ว
    const [loadingPw, setLoadingPw] = useState(null); // reqId กำลังโหลดรหัสผ่าน
    const [newPw, setNewPw] = useState({}); // { [reqId]: ค่ารหัสใหม่ที่เจ้าหน้าที่กำลังกรอก (เคสเก่า) }

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/account-requests', { params: { status: filter } });
            setRequests(res.data || []);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const formatCid = (cid) =>
        cid ? cid.replace(/(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/, '$1-$2-$3-$4-$5') : '-';

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const handleApprove = async (req) => {
        const emailLine = req.email
            ? `ระบบจะส่งอีเมลแจ้งผู้ใช้ (${req.email})`
            : `⚠️ คำขอนี้ยังไม่มีอีเมล จะไม่มีการแจ้งผู้ใช้ทางอีเมล\n(ให้ผู้ใช้ไปกรอกอีเมลเองที่หน้า "ตรวจสอบสถานะ" ก่อน)`;
        const ok = window.confirm(
            `อนุมัติและสร้างบัญชีให้ "${req.fullname}" (${formatCid(req.cid)})?\n\n` +
            `ระบบจะสร้างบัญชีให้ทันทีถ้าพบข้อมูลพนักงานในฐานเงินเดือน\n` +
            `ผู้ใช้เข้าระบบได้เลยด้วยเลขบัตร + รหัสผ่านที่ตั้งไว้ตอนยื่นคำขอ\n` +
            `(ถ้ายังไม่พบข้อมูลพนักงาน จะเป็นการ "รับเรื่อง" รอเพิ่มข้อมูลในระบบเงินเดือนก่อน)\n\n` +
            emailLine
        );
        if (!ok) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/account-requests/${req.id}/approve`);
            setMessage({ type: 'success', text: res.data?.message || `รับเรื่องคำขอของ ${req.fullname} แล้ว` });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (req) => {
        const reason = window.prompt(`เหตุผลที่ปฏิเสธคำขอของ "${req.fullname}" (ไม่บังคับ):`, '');
        if (reason === null) return; // กดยกเลิก
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            await api.post(`/admin/account-requests/${req.id}/reject`, { admin_note: reason });
            setMessage({ type: 'success', text: 'ปฏิเสธคำขอแล้ว' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    // ยืนยันว่าเพิ่มผู้ใช้เข้าระบบหลังบ้านแล้ว → ส่งอีเมลแจ้งว่าบัญชีพร้อมใช้งาน
    const handleComplete = async (req) => {
        const ok = window.confirm(
            `ยืนยันว่าได้เพิ่ม "${req.fullname}" เข้าระบบ (เงินเดือน/Slip Online) เรียบร้อยแล้ว?\n\n` +
            (req.email
                ? `ระบบจะส่งอีเมลแจ้งผู้ใช้ว่าบัญชีพร้อมใช้งาน — เข้าสู่ระบบได้ (${req.email})`
                : '⚠️ คำขอนี้ไม่มีอีเมล จะไม่มีการแจ้งทางอีเมล')
        );
        if (!ok) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/account-requests/${req.id}/complete`);
            setMessage({ type: 'success', text: res.data?.message || 'ยืนยันเพิ่มเข้าระบบแล้ว' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const handleResendEmail = async (req) => {
        if (!window.confirm(`ส่งอีเมล "กำลังดำเนินการ" ไปที่ ${req.email} อีกครั้ง?`)) return;
        setResendingId(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/account-requests/${req.id}/resend-email`);
            setMessage({ type: 'success', text: res.data?.message || 'ส่งอีเมลแล้ว' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งอีเมลไม่สำเร็จ' });
        } finally {
            setResendingId(null);
        }
    };

    const handleRevealPassword = async (req) => {
        setLoadingPw(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.get(`/admin/account-requests/${req.id}/password`);
            setRevealedPw(prev => ({ ...prev, [req.id]: { password: res.data.password, encrypted: !!res.data.encrypted } }));
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดูรหัสผ่านไม่สำเร็จ' });
        } finally {
            setLoadingPw(null);
        }
    };

    const handleHidePassword = (reqId) => {
        setRevealedPw(prev => { const n = { ...prev }; delete n[reqId]; return n; });
        setNewPw(prev => { const n = { ...prev }; delete n[reqId]; return n; });
    };

    // เคสเก่า (รหัสถูก bcrypt ดึงไม่ได้): เจ้าหน้าที่ยืนยันรหัสกับผู้ใช้แล้วบันทึกเป็น plain text
    const handleSetPassword = async (req) => {
        const val = (newPw[req.id] || '').trim();
        if (val.length < 4) {
            setMessage({ type: 'error', text: 'กรุณากำหนดรหัสผ่าน อย่างน้อย 4 หลัก' });
            return;
        }
        setLoadingPw(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/account-requests/${req.id}/set-password`, { password: val });
            setRevealedPw(prev => ({ ...prev, [req.id]: { password: res.data.password, encrypted: false } }));
            setNewPw(prev => { const n = { ...prev }; delete n[req.id]; return n; });
            setMessage({ type: 'success', text: res.data.message || 'บันทึกรหัสผ่านแล้ว' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'บันทึกรหัสผ่านไม่สำเร็จ' });
        } finally {
            setLoadingPw(null);
        }
    };

    const handleCopyPassword = async (pw) => {
        try {
            await navigator.clipboard.writeText(pw);
            setMessage({ type: 'success', text: 'คัดลอกรหัสผ่านแล้ว' });
        } catch (e) {
            setMessage({ type: 'error', text: 'คัดลอกไม่สำเร็จ — กรุณาคัดลอกด้วยตนเอง' });
        }
    };

    // เส้นลิงก์ปุ่มเล็ก ๆ
    const linkBtnStyle = { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' };

    // แสดงรหัสผ่านที่ผู้ใช้ตั้งไว้ (กดดู + คัดลอก) — สำหรับเจ้าหน้าที่นำไปสร้างบัญชีในระบบหลังบ้าน
    const renderPasswordSection = (req) => {
        if (req.status === 'rejected' || !req.has_password) return null;
        const entry = revealedPw[req.id];
        const inputStyle = { background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', fontSize: 15, minWidth: 200 };
        return (
            <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ color: '#1e40af', fontSize: 13, marginBottom: 8 }}>
                        🔑 รหัสผ่านที่ผู้ใช้ตั้งไว้ — นำไปสร้างบัญชีในระบบหลังบ้าน (Slip Online)
                    </div>
                    {entry === undefined ? (
                        <button
                            className="steam-btn steam-btn-secondary"
                            onClick={() => handleRevealPassword(req)}
                            disabled={loadingPw === req.id}
                        >
                            {loadingPw === req.id ? 'กำลังโหลด...' : 'แสดงรหัสผ่าน'}
                        </button>
                    ) : entry.encrypted ? (
                        <div>
                            <div style={{ color: '#b45309', fontSize: 13, marginBottom: 8, lineHeight: 1.6 }}>
                                🔒 คำขอนี้เป็นเคสเก่า รหัสถูกเข้ารหัสไว้ (สมัครก่อนเปลี่ยนระบบ) จึง<strong>ดึงรหัสเดิมไม่ได้</strong>
                                {' '}— โปรดติดต่อผู้ใช้ตามเบอร์ที่แจ้งเพื่อยืนยันรหัส แล้วบันทึกเป็นรหัสที่จะใช้สร้างบัญชีด้านล่าง
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                    type="text"
                                    value={newPw[req.id] || ''}
                                    onChange={(e) => setNewPw(prev => ({ ...prev, [req.id]: e.target.value }))}
                                    placeholder="รหัสผ่านที่จะใช้ (อย่างน้อย 4 หลัก)"
                                    style={inputStyle}
                                />
                                <button
                                    className="steam-btn steam-btn-primary"
                                    onClick={() => handleSetPassword(req)}
                                    disabled={loadingPw === req.id}
                                >
                                    {loadingPw === req.id ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่าน'}
                                </button>
                                <button onClick={() => handleHidePassword(req.id)} style={linkBtnStyle}>ซ่อน</button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <code style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', fontSize: 15, letterSpacing: 1, userSelect: 'all' }}>{entry.password}</code>
                            <button onClick={() => handleCopyPassword(entry.password)} style={linkBtnStyle}>คัดลอก</button>
                            <button onClick={() => handleHidePassword(req.id)} style={linkBtnStyle}>ซ่อน</button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // แสดงส่วนอีเมล (อ่านอย่างเดียว) — ผู้ใช้ต้องกรอก/ยืนยันอีเมลเองที่หน้า "ตรวจสอบสถานะ"
    const renderEmailSection = (req) => {
        if (req.email) {
            const canResend = req.status === 'approved';
            return (
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span><span style={{ color: 'var(--steam-text-muted)' }}>อีเมล:</span> {req.email}</span>
                    {canResend && (
                        <button onClick={() => handleResendEmail(req)} disabled={resendingId === req.id} style={linkBtnStyle}>
                            {resendingId === req.id ? 'กำลังส่ง...' : 'ส่งอีเมลแจ้งซ้ำ'}
                        </button>
                    )}
                </div>
            );
        }
        // ยังไม่มีอีเมล — รอผู้ใช้กรอกเอง (เจ้าหน้าที่กรอกแทนไม่ได้)
        if (req.status === 'pending') {
            return (
                <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', color: '#b91c1c', fontSize: 13 }}>
                        ⚠️ ยังไม่มีอีเมล — ให้ผู้ใช้กรอกและยืนยันอีเมลเองที่หน้า “ตรวจสอบสถานะ” (กรอกเลขบัตร → เพิ่มอีเมล)
                    </div>
                </div>
            );
        }
        return <div><span style={{ color: 'var(--steam-text-muted)' }}>อีเมล:</span> -</div>;
    };

    const statusBadge = (status) => {
        const map = {
            pending: { cls: 'warning', text: 'รอดำเนินการ' },
            approved: { cls: 'primary', text: 'รับเรื่องแล้ว (รอเพิ่มข้อมูล)' },
            completed: { cls: 'success', text: 'เปิดบัญชีแล้ว' },
            rejected: { cls: 'danger', text: 'ปฏิเสธ' }
        };
        const b = map[status] || { cls: 'secondary', text: status };
        return <span className={`badge badge-${b.cls}`}>{b.text}</span>;
    };

    const filters = [
        { key: 'pending', label: 'รอดำเนินการ' },
        { key: 'approved', label: 'รับเรื่องแล้ว' },
        { key: 'completed', label: 'เปิดบัญชีแล้ว' },
        { key: 'rejected', label: 'ปฏิเสธ' },
        { key: 'all', label: 'ทั้งหมด' }
    ];

    // คำขอที่รอดำเนินการแต่ยังไม่มีอีเมล (สมัครก่อนระบบจะมีอีเมล)
    const noEmailCount = requests.filter(r => r.status === 'pending' && !r.email).length;

    return (
        <div className="admin-page">
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>คำขอเปิดบัญชี</span>
            </div>

            <div className="admin-header">
                <h1>คำขอเปิดบัญชี</h1>
                <p>ตรวจเอกสารแล้วอนุมัติ — ระบบสร้างบัญชีให้ทันที</p>
            </div>

            <div
                style={{
                    background: 'var(--steam-bg-card)',
                    border: '1px solid rgba(255, 193, 7, 0.35)',
                    borderLeft: '4px solid #f0a500',
                    borderRadius: 'var(--steam-radius-md, 8px)',
                    padding: '14px 18px',
                    marginBottom: 20,
                    lineHeight: 1.7,
                    color: 'var(--steam-text-primary)',
                }}
            >
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 17, lineHeight: 1.5 }}>✅</span>
                    <div>
                        <strong>พบข้อมูลพนักงานในฐานเงินเดือน</strong> — กด “สร้างบัญชี” = เปิดบัญชีให้ทันที
                        <div style={{ fontSize: 13, color: 'var(--steam-text-secondary)', marginTop: 2 }}>
                            ตรวจเอกสารแนบให้เรียบร้อยแล้วกดอนุมัติได้เลย ผู้ใช้เข้าระบบได้ทันทีด้วยเลขบัตร + รหัสที่ตั้งไว้
                            (ระบบส่งอีเมลแจ้งว่า “บัญชีพร้อมใช้งาน”)
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                    <span style={{ fontSize: 17, lineHeight: 1.5 }}>⏳</span>
                    <div>
                        <strong>ยังไม่พบข้อมูลพนักงาน</strong> — เป็นการรับเรื่องไว้ก่อน
                        <div style={{ fontSize: 13, color: 'var(--steam-text-secondary)', marginTop: 2 }}>
                            เพิ่มข้อมูลใน<b>โปรแกรมเงินเดือน</b>ก่อน แล้วกลับมากด “ยืนยันเพิ่มเข้าระบบแล้ว”
                        </div>
                    </div>
                </div>
            </div>

            {noEmailCount > 0 && (
                <div
                    className="alert alert-danger"
                    style={{ display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.6 }}
                >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>📧</span>
                    <div>
                        <strong>มี {noEmailCount} คำขอที่ยังไม่มีอีเมล</strong>
                        <div style={{ fontSize: 13, marginTop: 4 }}>
                            คำขอเหล่านี้สมัครก่อนระบบจะมีอีเมล — <b>ให้ผู้ใช้ไปกรอกและยืนยันอีเมลเอง</b>ที่หน้า “ตรวจสอบสถานะ”
                            (กรอกเลขบัตร → เพิ่มอีเมล) เจ้าหน้าที่กรอกแทนไม่ได้
                        </div>
                    </div>
                </div>
            )}

            {message.text && (
                <div className={`alert alert-${message.type}`}>{message.text}</div>
            )}

            <div className="filter-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`steam-btn ${filter === f.key ? 'steam-btn-primary' : 'steam-btn-secondary'}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex-center" style={{ padding: '60px 0' }}>
                    <div className="spinner"></div>
                </div>
            ) : requests.length === 0 ? (
                <div className="steam-card">
                    <div className="steam-card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--steam-text-muted)' }}>
                        ไม่มีคำขอในสถานะนี้
                    </div>
                </div>
            ) : (
                <div className="account-requests-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {requests.map(req => (
                        <div key={req.id} className="steam-card">
                            <div className="steam-card-body">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 240 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                                            <h3 style={{ margin: 0, fontSize: 17 }}>{req.fullname}</h3>
                                            {statusBadge(req.status)}
                                        </div>
                                        <div className="ar-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 20px', fontSize: 14 }}>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>เลขบัตร:</span> {formatCid(req.cid)}</div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>เบอร์โทร:</span> {req.phone || '-'}</div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>หน่วยงาน:</span> {req.department || '-'}</div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>ยื่นเมื่อ:</span> {formatDate(req.created_at)}</div>
                                            {renderEmailSection(req)}
                                            {renderPasswordSection(req)}
                                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                                                <div>
                                                    <span style={{ color: 'var(--steam-text-muted)', display: 'block', marginBottom: 4 }}>รูปถือบัตร (เทียบหน้า):</span>
                                                    {req.has_face ? (
                                                        <IdCardThumb
                                                            reqId={req.id}
                                                            img="face"
                                                            onZoom={(reqId, img) => setLightbox({ reqId, img, fullname: req.fullname })}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#d97706' }}>ไม่มี (คำขอก่อนบังคับแนบ)</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <span style={{ color: 'var(--steam-text-muted)', display: 'block', marginBottom: 4 }}>รูปบัตรประชาชน:</span>
                                                    {req.has_id_card ? (
                                                        <IdCardThumb
                                                            reqId={req.id}
                                                            onZoom={(reqId, img) => setLightbox({ reqId, img, fullname: req.fullname })}
                                                        />
                                                    ) : (
                                                        <span style={{ color: '#dc2626' }}>ไม่มี</span>
                                                    )}
                                                </div>
                                            </div>
                                            {req.note && (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <span style={{ color: 'var(--steam-text-muted)' }}>หมายเหตุ:</span> {req.note}
                                                </div>
                                            )}
                                            {req.status !== 'pending' && (
                                                <div style={{ gridColumn: '1 / -1', color: 'var(--steam-text-muted)' }}>
                                                    ดำเนินการโดย: {req.processed_by_name || '-'} • {formatDate(req.processed_at)}
                                                    {req.admin_note ? ` • หมายเหตุ: ${req.admin_note}` : ''}
                                                </div>
                                            )}
                                            {(req.status === 'approved' || req.status === 'completed') && (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <span style={{ color: 'var(--steam-text-muted)' }}>เข้าระบบล่าสุด:</span>{' '}
                                                    {!req.has_account ? (
                                                        <span style={{ color: 'var(--steam-text-muted)' }}>ยังไม่พบบัญชีในระบบ</span>
                                                    ) : req.user_last_login ? (
                                                        <span style={{ color: '#16a34a', fontWeight: 600 }}>{formatDate(req.user_last_login)}</span>
                                                    ) : (
                                                        <span style={{ color: '#d97706', fontWeight: 600 }}>ยังไม่เคยเข้าระบบ</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {req.status === 'pending' && (
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                className="steam-btn steam-btn-primary"
                                                onClick={() => handleApprove(req)}
                                                disabled={processing === req.id}
                                            >
                                                {processing === req.id ? '...' : '✅ สร้างบัญชี'}
                                            </button>
                                            <button
                                                className="steam-btn steam-btn-danger"
                                                onClick={() => handleReject(req)}
                                                disabled={processing === req.id}
                                            >
                                                ปฏิเสธ
                                            </button>
                                        </div>
                                    )}

                                    {req.status === 'approved' && (
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                className="steam-btn steam-btn-primary"
                                                onClick={() => handleComplete(req)}
                                                disabled={processing === req.id}
                                                title="ยืนยันว่าเพิ่มผู้ใช้เข้าระบบเงินเดือน/Slip Online แล้ว และแจ้งอีเมลผู้ใช้"
                                            >
                                                {processing === req.id ? '...' : '✅ ยืนยันเพิ่มเข้าระบบแล้ว'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {lightbox && (
                <IdCardLightbox
                    reqId={lightbox.reqId}
                    fullname={lightbox.fullname}
                    img={lightbox.img || 'card'}
                    onClose={() => setLightbox(null)}
                />
            )}
        </div>
    );
}

export default AccountRequests;
